import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ASSIGNMENT_CHUNK_TYPES,
  detectChunkDataMissing,
  getAssignmentLogs,
  getDriverEligibilityPage,
  loadSessionChunksBatch,
  mergeChunksToNormalized,
  projectDriversForMapsList,
} from '../services/firebaseService';
import { assignmentChunkKeys } from './assignmentChunkKeys';

const TRIPS_LITE_TYPES = [
  ASSIGNMENT_CHUNK_TYPES.METADATA,
  ASSIGNMENT_CHUNK_TYPES.DRIVERS,
];

const TRIPS_FULL_TYPES = [
  ASSIGNMENT_CHUNK_TYPES.METADATA,
  ASSIGNMENT_CHUNK_TYPES.DRIVERS,
  ASSIGNMENT_CHUNK_TYPES.UNASSIGNED_TRIPS,
];

const PORTAL_CHUNK_TYPES = [
  ASSIGNMENT_CHUNK_TYPES.ORIGINAL_PLAN,
  ASSIGNMENT_CHUNK_TYPES.METADATA,
];

const CHARTS_CHUNK_TYPES = [
  ASSIGNMENT_CHUNK_TYPES.METADATA,
  ASSIGNMENT_CHUNK_TYPES.DRIVERS,
  ASSIGNMENT_CHUNK_TYPES.UNASSIGNED_TRIPS,
];

const MAPS_CHUNK_TYPES = [
  ASSIGNMENT_CHUNK_TYPES.DRIVERS,
  ASSIGNMENT_CHUNK_TYPES.METADATA,
];

const MULTILOAD_TYPES = [ASSIGNMENT_CHUNK_TYPES.MULTILOAD_ROUTES];

export function isAssignmentSessionQueryEnabled(resultId) {
  return Boolean(resultId) && !String(resultId).startsWith('mock-');
}

function applySessionOverride(chunks, sessionOverride) {
  if (!sessionOverride) return chunks || {};
  const next = { ...(chunks || {}) };
  if (sessionOverride.drivers !== undefined) {
    next[ASSIGNMENT_CHUNK_TYPES.DRIVERS] = sessionOverride.drivers;
  }
  if (sessionOverride.unassigned_trips !== undefined) {
    next[ASSIGNMENT_CHUNK_TYPES.UNASSIGNED_TRIPS] = sessionOverride.unassigned_trips;
  }
  if (sessionOverride.multiload_routes !== undefined) {
    next[ASSIGNMENT_CHUNK_TYPES.MULTILOAD_ROUTES] = sessionOverride.multiload_routes;
  }
  return next;
}

function useChunkBundle(resultId, chunkTypes, options = {}) {
  const queryClient = useQueryClient();
  const enabled = (options.enabled ?? true) && isAssignmentSessionQueryEnabled(resultId);
  const chunkTypesKey = (chunkTypes || []).length ? (chunkTypes || []).join(',') : '_shell';
  const bundleStage = options.bundleStage || 'full';
  const driversLite = Boolean(options.driversLite);

  const bundleQuery = useQuery({
    queryKey: assignmentChunkKeys.bundle(resultId, chunkTypesKey, bundleStage),
    queryFn: () =>
      loadSessionChunksBatch(resultId, chunkTypes, { driversLite }),
    enabled,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    structuralSharing: false,
  });

  const { data: sessionOverride } = useQuery({
    queryKey: assignmentChunkKeys.override(resultId),
    queryFn: () => queryClient.getQueryData(assignmentChunkKeys.override(resultId)) ?? {},
    enabled: enabled && Boolean(resultId),
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!resultId || !bundleQuery.data?.mainDoc) return;
    queryClient.setQueryData(assignmentChunkKeys.main(resultId), bundleQuery.data.mainDoc);
    const mergedChunks = applySessionOverride(bundleQuery.data.chunks, sessionOverride);
    (chunkTypes || []).forEach((type) => {
      queryClient.setQueryData(
        assignmentChunkKeys.chunk(resultId, type),
        mergedChunks[type] ?? null
      );
    });
  }, [bundleQuery.data, sessionOverride, resultId, chunkTypesKey, chunkTypes, queryClient]);

  const chunkMap = useMemo(
    () => applySessionOverride(bundleQuery.data?.chunks, sessionOverride),
    [bundleQuery.data?.chunks, sessionOverride]
  );

  const normalized = useMemo(() => {
    if (!bundleQuery.data?.mainDoc) return null;
    let merged = mergeChunksToNormalized(bundleQuery.data.mainDoc, chunkMap);
    if (sessionOverride?.publishFlags) {
      merged = { ...merged, ...sessionOverride.publishFlags };
    }
    return merged;
  }, [bundleQuery.data?.mainDoc, chunkMap, sessionOverride?.publishFlags]);

  const chunkDataMissing = useMemo(() => {
    if (!bundleQuery.data?.mainDoc) return false;
    return detectChunkDataMissing(
      bundleQuery.data.mainDoc,
      ASSIGNMENT_CHUNK_TYPES.DRIVERS,
      chunkMap[ASSIGNMENT_CHUNK_TYPES.DRIVERS]
    );
  }, [bundleQuery.data?.mainDoc, chunkMap]);

  return {
    mainDoc: bundleQuery.data?.mainDoc ?? null,
    chunkMap,
    normalized,
    isLoading: bundleQuery.isLoading,
    isFetching: bundleQuery.isFetching,
    error: bundleQuery.error ?? null,
    chunkDataMissing,
  };
}

function mergeStagedBundles(shell, lite, full) {
  const mainDoc = full.mainDoc || lite.mainDoc || shell.mainDoc;
  const chunkMap = {
    ...shell.chunkMap,
    ...lite.chunkMap,
    ...full.chunkMap,
  };
  if (!mainDoc) return { mainDoc: null, chunkMap, normalized: null };

  let normalized = mergeChunksToNormalized(mainDoc, chunkMap);
  if (full.normalized?.unassigned_trips) {
    normalized = {
      ...normalized,
      drivers: full.normalized.drivers ?? normalized?.drivers,
      unassigned_trips: full.normalized.unassigned_trips,
    };
  } else if (lite.normalized?.drivers) {
    normalized = {
      ...normalized,
      drivers: lite.normalized.drivers,
    };
  }
  return { mainDoc, chunkMap, normalized };
}

/**
 * Trips: staged load — shell (summary/metadata) → lite drivers → full trips.
 * multiload_routes loaded separately via useMultiloadRoutes (same as Bucket/Maps).
 */
export function useTripsAssignment(resultId, options = {}) {
  const enabled = (options.enabled ?? true) && isAssignmentSessionQueryEnabled(resultId);

  const shell = useChunkBundle(resultId, [], {
    ...options,
    enabled,
    bundleStage: 'shell',
  });

  const liteEnabled = enabled && Boolean(shell.mainDoc) && !shell.isLoading;
  const lite = useChunkBundle(resultId, TRIPS_LITE_TYPES, {
    ...options,
    enabled: liteEnabled,
    bundleStage: 'lite',
    driversLite: true,
  });

  const fullEnabled =
    enabled && Boolean(lite.mainDoc) && !lite.isLoading && Boolean(lite.chunkMap?.drivers);
  const full = useChunkBundle(resultId, TRIPS_FULL_TYPES, {
    ...options,
    enabled: fullEnabled,
    bundleStage: 'full',
  });

  const merged = useMemo(
    () => mergeStagedBundles(shell, lite, full),
    [shell.mainDoc, shell.chunkMap, shell.normalized, lite.mainDoc, lite.chunkMap, lite.normalized, full.mainDoc, full.chunkMap, full.normalized]
  );

  const loadStage = full.normalized?.drivers?.some((d) => (d.trips || []).length > 0)
    ? 'full'
    : lite.normalized?.drivers?.length
      ? 'lite'
      : shell.normalized
        ? 'shell'
        : 'idle';

  return {
    mainDoc: merged.mainDoc,
    chunkMap: merged.chunkMap,
    normalized: merged.normalized,
    loadStage,
    isLoading: shell.isLoading && !shell.normalized,
    isLoadingDetails: loadStage === 'shell' || loadStage === 'lite',
    isFetching: shell.isFetching || lite.isFetching || full.isFetching,
    error: shell.error && !shell.normalized
      ? shell.error
      : full.error && !merged.normalized
        ? full.error
        : null,
    chunkDataMissing: Boolean(full.mainDoc) && !full.isLoading && full.chunkDataMissing,
  };
}

/** multiload_routes — Trips / Bucket / Maps. */
export function useMultiloadRoutes(resultId, options = {}) {
  return useChunkBundle(resultId, MULTILOAD_TYPES, { ...options, bundleStage: 'multiload' });
}

/** Publish: merge trips + portal original_plan when both loaded. */
export function useMergedAssignmentSession(resultId, options = {}) {
  const trips = useTripsAssignment(resultId, options);
  const portal = usePortalAssignment(resultId, options);

  const normalized = useMemo(() => {
    const t = trips.normalized;
    const p = portal.normalized;
    if (!t && !p) return null;
    if (!t) return p;
    if (!p?.original_plan) return t;
    return { ...t, original_plan: p.original_plan };
  }, [trips.normalized, portal.normalized]);

  return { normalized, trips, portal };
}

export function usePortalAssignment(resultId, options = {}) {
  return useChunkBundle(resultId, PORTAL_CHUNK_TYPES, options);
}

export function useChartsAssignment(resultId, options = {}) {
  return useChunkBundle(resultId, CHARTS_CHUNK_TYPES, options);
}

export function useAssignmentMainDoc(resultId, options = {}) {
  return useChunkBundle(resultId, [], { ...options, bundleStage: 'shell' });
}

export function useAssignmentChunk(resultId, chunkType, options = {}) {
  const enabled =
    (options.enabled ?? true) &&
    isAssignmentSessionQueryEnabled(resultId) &&
    Boolean(chunkType);
  return useQuery({
    queryKey: assignmentChunkKeys.chunk(resultId, chunkType),
    queryFn: async () => {
      const { chunks } = await loadSessionChunksBatch(resultId, [chunkType]);
      return chunks[chunkType] ?? null;
    },
    enabled,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
  });
}

/** Logs — single background fetch when session is selected. */
export function useAssignmentLogs(resultId, options = {}) {
  const enabled = (options.enabled ?? true) && isAssignmentSessionQueryEnabled(resultId);
  return useQuery({
    queryKey: [...assignmentChunkKeys.chunk(resultId, 'logs'), 'full'],
    queryFn: () => getAssignmentLogs(resultId),
    enabled,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
  });
}

/** Eligibility — single background fetch when session is selected. */
export function useDriverEligibility(resultId, options = {}) {
  const enabled = (options.enabled ?? true) && isAssignmentSessionQueryEnabled(resultId);
  return useQuery({
    queryKey: [...assignmentChunkKeys.chunk(resultId, 'eligibility'), 'full'],
    queryFn: () => getDriverEligibilityPage(resultId),
    enabled,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
  });
}

export function useMapsAssignment(resultId, options = {}) {
  const bundle = useChunkBundle(resultId, MAPS_CHUNK_TYPES, options);

  const liteNormalized = useMemo(() => {
    if (!bundle.mainDoc) return null;
    const fullDrivers = bundle.chunkMap[ASSIGNMENT_CHUNK_TYPES.DRIVERS];
    return mergeChunksToNormalized(bundle.mainDoc, {
      [ASSIGNMENT_CHUNK_TYPES.METADATA]: bundle.chunkMap[ASSIGNMENT_CHUNK_TYPES.METADATA],
      [ASSIGNMENT_CHUNK_TYPES.DRIVERS]: projectDriversForMapsList(fullDrivers),
    });
  }, [bundle.mainDoc, bundle.chunkMap]);

  return { ...bundle, normalized: liteNormalized };
}

export function useInvalidateAssignmentSession() {
  const queryClient = useQueryClient();

  return useCallback(
    (resultId) => {
      if (resultId) {
        queryClient.removeQueries({ queryKey: assignmentChunkKeys.override(resultId) });
        return queryClient.invalidateQueries({
          queryKey: assignmentChunkKeys.forResult(resultId),
        });
      }
      queryClient.removeQueries({ queryKey: assignmentChunkKeys.all });
      return queryClient.invalidateQueries({
        queryKey: assignmentChunkKeys.all,
      });
    },
    [queryClient]
  );
}

export function useRefreshAssignmentSession() {
  const queryClient = useQueryClient();

  return useCallback(
    async (resultId) => {
      if (!resultId || !isAssignmentSessionQueryEnabled(resultId)) return;
      queryClient.removeQueries({ queryKey: assignmentChunkKeys.override(resultId) });
      await queryClient.invalidateQueries({
        queryKey: assignmentChunkKeys.forResult(resultId),
      });
      await queryClient.refetchQueries({
        queryKey: assignmentChunkKeys.forResult(resultId),
        type: 'active',
      });
    },
    [queryClient]
  );
}

export function patchAssignmentSessionCache(queryClient, resultId, patch = {}) {
  if (!resultId || !queryClient) return;

  const {
    drivers,
    unassigned_trips,
    multiload_routes,
    invalidateLogs = false,
    publishFlags = null,
  } = patch;

  queryClient.cancelQueries({ queryKey: assignmentChunkKeys.forResult(resultId) });

  queryClient.setQueryData(assignmentChunkKeys.override(resultId), (old) => {
    const prev = old || {};
    const next = { ...prev };
    if (drivers !== undefined) next.drivers = drivers;
    if (unassigned_trips !== undefined) next.unassigned_trips = unassigned_trips;
    if (multiload_routes !== undefined) next.multiload_routes = multiload_routes;
    if (publishFlags) {
      next.publishFlags = { ...(prev.publishFlags || {}), ...publishFlags };
    }
    return next;
  });

  const applyChunkPatch = (chunks) => {
    const next = { ...(chunks || {}) };
    if (drivers !== undefined) next[ASSIGNMENT_CHUNK_TYPES.DRIVERS] = drivers;
    if (unassigned_trips !== undefined) {
      next[ASSIGNMENT_CHUNK_TYPES.UNASSIGNED_TRIPS] = unassigned_trips;
    }
    if (multiload_routes !== undefined) {
      next[ASSIGNMENT_CHUNK_TYPES.MULTILOAD_ROUTES] = multiload_routes;
    }
    return next;
  };

  const applyMainDocPatch = (mainDoc) => {
    if (!publishFlags || !mainDoc) return mainDoc;
    return {
      ...mainDoc,
      ...publishFlags,
      results: { ...(mainDoc.results || {}), ...publishFlags },
    };
  };

  if (drivers !== undefined) {
    queryClient.setQueryData(
      assignmentChunkKeys.chunk(resultId, ASSIGNMENT_CHUNK_TYPES.DRIVERS),
      drivers
    );
  }
  if (unassigned_trips !== undefined) {
    queryClient.setQueryData(
      assignmentChunkKeys.chunk(resultId, ASSIGNMENT_CHUNK_TYPES.UNASSIGNED_TRIPS),
      unassigned_trips
    );
  }
  if (multiload_routes !== undefined) {
    queryClient.setQueryData(
      assignmentChunkKeys.chunk(resultId, ASSIGNMENT_CHUNK_TYPES.MULTILOAD_ROUTES),
      multiload_routes
    );
  }

  queryClient.setQueriesData(
    { queryKey: assignmentChunkKeys.forResult(resultId) },
    (old) => {
      if (!old || typeof old !== 'object' || !old.mainDoc) return old;
      return {
        mainDoc: applyMainDocPatch(old.mainDoc),
        chunks: applyChunkPatch(old.chunks),
      };
    }
  );

  if (publishFlags) {
    queryClient.setQueryData(assignmentChunkKeys.main(resultId), (old) =>
      applyMainDocPatch(old)
    );
  }

  if (invalidateLogs) {
    queryClient.invalidateQueries({
      queryKey: assignmentChunkKeys.chunk(resultId, 'logs'),
    });
  }
}

export function usePatchAssignmentSessionCache() {
  const queryClient = useQueryClient();
  return useCallback(
    (resultId, patch = {}) => patchAssignmentSessionCache(queryClient, resultId, patch),
    [queryClient]
  );
}

export function useAssignmentSession(resultId) {
  const trips = useTripsAssignment(resultId);
  return {
    data: trips.normalized
      ? {
          normalized: trips.normalized,
          logs: null,
          chunkDataMissing: trips.chunkDataMissing,
        }
      : undefined,
    isLoading: trips.isLoading,
    isFetching: trips.isFetching,
    error: trips.error,
  };
}
